/**
 * The 1.5× budget guard (SECURITY §3): a job pauses when its actual cost passes 1.5× the confirmed estimate. Pure state plus a
 * provider wrapper; the UI decides whether to raise the limit and resume.
 */
import { costOfUsage } from '../../engine/bench/cost';
import type { CompletionRequest, CompletionResponse, Provider } from '../../engine/bench/types';
import { BudgetPausedError } from './errors';

export const BUDGET_FACTOR = 1.5;

export interface BudgetState {
	spentUsd: number;
	limitUsd: number;
	paused: boolean;
}

export interface BudgetGuard {
	/** The provider to hand to `runBench`. */
	provider: Provider;
	/** Aborted when the guard pauses, so `runBench` stops between chunks. */
	readonly signal: AbortSignal;
	state(): BudgetState;
	/** Records usage (also called by the wrapper). */
	record(model: string, usage: CompletionResponse['usage']): BudgetState;
	/** Adds a cost already worked out (an Economy round, whose answers arrive as a batch). */
	add(usd: number): BudgetState;
	/** Raises the limit and clears the pause (the customer confirmed continuing). Returns a fresh signal via `signal`. */
	resume(newLimitUsd: number): BudgetState;
}

/**
 * The limit after a "continue": the current limit raised once by the factor (spec §15 item 5: the limit compounded about 2.25×
 * per resume because the run restarted from the estimate and the dialog multiplied again).
 */
export const nextLimit = (limitUsd: number, factor = BUDGET_FACTOR): number => limitUsd * factor;

/** `limit = factor × estimate`. A non-positive estimate never pauses on cost alone (nothing was promised). */
export function limitFor(estimateUsd: number, factor = BUDGET_FACTOR): number {
	return estimateUsd * factor;
}

export function createBudgetGuard(
	inner: Provider,
	estimateUsd: number,
	factor = BUDGET_FACTOR,
	/** A resumed job: what it already spent and its limit (the limit is not recomputed from the estimate). */
	carried?: { spentUsd: number; limitUsd: number }
): BudgetGuard {
	let controller = new AbortController();
	const s: BudgetState = carried
		? { spentUsd: carried.spentUsd, limitUsd: carried.limitUsd, paused: false }
		: { spentUsd: 0, limitUsd: limitFor(estimateUsd, factor), paused: false };

	const check = () => {
		if (!s.paused && s.limitUsd > 0 && s.spentUsd > s.limitUsd) {
			s.paused = true;
			controller.abort();
		}
	};

	const guard: BudgetGuard = {
		get signal() {
			return controller.signal;
		},
		state: () => ({ ...s }),
		record(model, usage) {
			s.spentUsd += costOfUsage(model, usage);
			check();
			return { ...s };
		},
		add(usd) {
			s.spentUsd += usd;
			check();
			return { ...s };
		},
		resume(newLimitUsd) {
			s.limitUsd = newLimitUsd;
			s.paused = false;
			controller = new AbortController();
			check();
			return { ...s };
		},
		provider: {
			async complete(req: CompletionRequest): Promise<CompletionResponse> {
				check();
				if (s.paused) {
					throw new BudgetPausedError(
						`paused: spent $${s.spentUsd.toFixed(4)} is over the limit of $${s.limitUsd.toFixed(4)}`
					);
				}
				const res = await inner.complete(req);
				guard.record(req.model, res.usage);
				return res;
			},
		},
	};
	return guard;
}
